import React, { useEffect, useRef, useState, useCallback } from "react";
import apiService from "../services/api";
import { ManageColumnsModal, ColumnConfig } from "./modals/ManageColumnsModal";

const LS_WIDTHS_KEY = "mobile-tracking-column-widths";
const LS_COLS_KEY = "mobile-tracking-columns";

const FIXED_COLUMNS = ["created_at", "time", "type"];

const COLUMN_LABELS: Record<string, string> = {
  created_at: "Created At",
  time: "Device Time",
  type: "Event Type",
};

const DEFAULT_WIDTHS: Record<string, number> = {
  created_at: 180,
  time: 180,
  type: 140,
};

type ColumnType = "date" | "integer" | "text";

interface ActiveFilter {
  id: string;
  column: string;
  operator: string;
  value: string;
  value2: string;
  columnType: ColumnType;
}

interface AddFilterState {
  column: string;
  operator: string;
  value: string;
  value2: string;
}

const DATE_OPS = [
  { value: "from", label: "From" },
  { value: "to", label: "To" },
];
const INT_OPS = [
  { value: "eq", label: "Equals (=)" },
  { value: "gt", label: "Greater than (>)" },
  { value: "lt", label: "Less than (<)" },
  { value: "between", label: "Between" },
];
const TEXT_OPS = [
  { value: "eq", label: "Equals" },
  { value: "contains", label: "Contains" },
];

function toLabel(col: string): string {
  return COLUMN_LABELS[col] ?? col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function loadWidths(): Record<string, number> {
  try {
    const s = localStorage.getItem(LS_WIDTHS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}

function loadColumns(): ColumnConfig[] | null {
  try {
    const s = localStorage.getItem(LS_COLS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return null;
}

function saveColumns(cols: ColumnConfig[]) {
  try { localStorage.setItem(LS_COLS_KEY, JSON.stringify(cols)); } catch {}
}

function buildColumns(allKeys: string[], existing: ColumnConfig[]): ColumnConfig[] {
  const existingIds = new Set(existing.map((c) => c.id));
  const newCols = allKeys
    .filter((k) => !existingIds.has(k))
    .map((k) => ({ id: k, label: toLabel(k), visible: true }));
  return [...existing, ...newCols];
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  try {
    // BigQuery DATETIME columns return "YYYY-MM-DD HH:MM:SS" with no timezone.
    // Treat bare datetime strings as UTC by appending Z.
    const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)
      ? value.replace(" ", "T") + "Z"
      : value;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-GB", { timeZone: "Asia/Jerusalem" });
  } catch { return String(value); }
}

function detectColumnType(col: string, sampleData: any[]): ColumnType {
  if (col === "created_at" || col === "time") return "date";
  if (/(_at|_date|_time)$/.test(col)) return "date";
  for (const row of sampleData) {
    const val = col === "type" ? row.type : row.data?.[col];
    if (val !== null && val !== undefined) {
      if (typeof val === "number") return "integer";
      if (typeof val === "string") {
        if (/^\d+(\.\d+)?$/.test(val.trim())) return "integer";
        if (/^\d{4}-\d{2}-\d{2}/.test(val)) return "date";
        return "text";
      }
    }
  }
  return "text";
}

function getOperators(type: ColumnType) {
  if (type === "date") return DATE_OPS;
  if (type === "integer") return INT_OPS;
  return TEXT_OPS;
}

function normalizeEventDate(val: string): Date {
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(val)
    ? val.replace(" ", "T") + "Z"
    : val;
  return new Date(normalized);
}

function applyFilters(rows: any[], activeFilters: ActiveFilter[]): any[] {
  if (activeFilters.length === 0) return rows;
  return rows.filter((row) =>
    activeFilters.every((f) => {
      let rawVal: any;
      if (f.column === "created_at") rawVal = row.created_at;
      else if (f.column === "time") rawVal = row.time;
      else if (f.column === "type") rawVal = row.type;
      else rawVal = row.data?.[f.column];

      if (rawVal === null || rawVal === undefined) return false;

      const isDateCol =
        f.column === "created_at" ||
        f.column === "time" ||
        /(_at|_date|_time)$/.test(f.column);
      const isDateVal =
        typeof rawVal === "string" && /^\d{4}-\d{2}-\d{2}/.test(rawVal);
      const isNumVal =
        typeof rawVal === "number" ||
        (typeof rawVal === "string" && /^\d+(\.\d+)?$/.test(rawVal.trim()));

      if (isDateCol || isDateVal) {
        const rowDate = normalizeEventDate(String(rawVal));
        if (isNaN(rowDate.getTime())) return false;
        if (f.operator === "from") return rowDate >= new Date(f.value + "T00:00:00Z");
        if (f.operator === "to") return rowDate <= new Date(f.value + "T23:59:59.999Z");
        return true;
      }

      if (isNumVal) {
        const numVal = typeof rawVal === "number" ? rawVal : parseFloat(String(rawVal));
        const numFilter = parseFloat(f.value);
        if (isNaN(numVal) || isNaN(numFilter)) return false;
        if (f.operator === "eq") return numVal === numFilter;
        if (f.operator === "gt") return numVal > numFilter;
        if (f.operator === "lt") return numVal < numFilter;
        if (f.operator === "between") {
          const num2 = parseFloat(f.value2);
          return !isNaN(num2) && numVal >= numFilter && numVal <= num2;
        }
        return true;
      }

      const strVal = String(rawVal).toLowerCase();
      if (f.operator === "eq") return strVal === f.value.toLowerCase();
      if (f.operator === "contains") return strVal.includes(f.value.toLowerCase());
      return true;
    })
  );
}

function formatFilterLabel(f: ActiveFilter): string {
  const col = toLabel(f.column);
  const opLabels: Record<string, string> = {
    from: "from", to: "to", eq: "=", gt: ">", lt: "<", between: "between", contains: "contains",
  };
  const op = opLabels[f.operator] ?? f.operator;
  if (f.operator === "between") return `${col}: ${f.value} – ${f.value2}`;
  return `${col} ${op} "${f.value}"`;
}

interface Props {
  token: string;
  patientId: number;
  patientName: string;
  onClose: () => void;
}

export const MobileTrackingPopup: React.FC<Props> = ({ token, patientId, patientName, onClose }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [addForm, setAddForm] = useState<AddFilterState | null>(null);
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);

  // Row detail
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // Columns: ordered list with visibility — persisted to localStorage
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = loadColumns();
    if (saved) return saved;
    return FIXED_COLUMNS.map((k) => ({ id: k, label: toLabel(k), visible: true }));
  });
  const [showColModal, setShowColModal] = useState(false);

  // Column widths
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadWidths);

  // Resize
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Merge newly discovered data-field columns into the columns list
  const mergeDataKeys = useCallback((rows: any[]) => {
    const discovered = new Set<string>();
    for (const ev of rows) {
      if (ev.data && typeof ev.data === "object") {
        Object.keys(ev.data).forEach((k) => discovered.add(k));
      }
    }
    if (discovered.size === 0) return;
    setColumns((prev) => {
      const updated = buildColumns([...FIXED_COLUMNS, ...discovered], prev);
      if (updated.length === prev.length) return prev;
      return updated;
    });
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getMobileTrackingEvents({
        token,
        patient_id: patientId,
        page,
        limit,
        sort_column: sortColumn,
        sort_direction: sortDirection,
        filters: filters.length > 0 ? JSON.stringify(filters) : undefined,
      });
      setEvents(res.events);
      setTotal(res.total);
      mergeDataKeys(res.events);
    } catch (e: any) {
      setError("Failed to load tracking data");
      console.error("[MobileTracking] fetch error", e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, patientId, page, limit, sortColumn, sortDirection, filters]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // SSE real-time
  useEffect(() => {
    const url = apiService.getMobileTrackingSSEUrl(token, patientId);
    const es = new EventSource(url);
    es.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.type === "new_event" && payload.event) {
          const ev = payload.event;
          mergeDataKeys([ev]);
          if (page === 1 && sortColumn === "created_at" && sortDirection === "DESC" && filters.length === 0) {
            setEvents((prev) => [ev, ...prev].slice(0, limit));
            setTotal((t) => t + 1);
          }
        }
      } catch {}
    };
    return () => es.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, patientId, page, sortColumn, sortDirection, limit, filters]);

  // Persist columns whenever they change
  useEffect(() => { saveColumns(columns); }, [columns]);

  // Persist widths
  useEffect(() => {
    try { localStorage.setItem(LS_WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [columnWidths]);

  // Resize mouse handling
  useEffect(() => {
    if (!resizingColumn) return;
    const onMove = (e: MouseEvent) => {
      const diff = e.clientX - resizeStartX.current;
      const newWidth = Math.max(60, resizeStartWidth.current + diff);
      setColumnWidths((prev) => ({ ...prev, [resizingColumn]: newWidth }));
    };
    const onUp = () => setResizingColumn(null);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [resizingColumn]);

  const handleResizeStart = (e: React.MouseEvent, col: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest("th");
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = th ? th.offsetWidth : (columnWidths[col] ?? DEFAULT_WIDTHS[col] ?? 120);
    setResizingColumn(col);
  };

  const SORTABLE = new Set(["created_at", "time", "type"]);

  const handleSort = (col: string) => {
    if (!SORTABLE.has(col)) return;
    if (sortColumn === col) {
      setSortDirection((d) => (d === "DESC" ? "ASC" : "DESC"));
    } else {
      setSortColumn(col);
      setSortDirection("DESC");
    }
    setPage(1);
  };

  const handleSaveColumns = (updated: ColumnConfig[]) => setColumns(updated);

  const handleResetColumns = () => {
    setColumns(FIXED_COLUMNS.map((k) => ({ id: k, label: toLabel(k), visible: true })));
  };

  // Filter handlers
  const openAddFilter = () => {
    const firstCol = columns[0]?.id ?? "created_at";
    const colType = detectColumnType(firstCol, events);
    const ops = getOperators(colType);
    setEditingFilterId(null);
    setAddForm({ column: firstCol, operator: ops[0].value, value: "", value2: "" });
  };

  const openEditFilter = (f: ActiveFilter) => {
    setEditingFilterId(f.id);
    setAddForm({ column: f.column, operator: f.operator, value: f.value, value2: f.value2 });
    setShowFilterPanel(true);
  };

  const handleAddFormColumnChange = (col: string) => {
    const colType = detectColumnType(col, events);
    const ops = getOperators(colType);
    setAddForm((f) => f ? { ...f, column: col, operator: ops[0].value, value: "", value2: "" } : null);
  };

  const handleConfirmAddFilter = () => {
    if (!addForm || !addForm.value) return;
    if (addForm.operator === "between" && !addForm.value2) return;
    if (editingFilterId) {
      setFilters((prev) => prev.map((f) =>
        f.id === editingFilterId
          ? { ...f, column: addForm.column, operator: addForm.operator, value: addForm.value, value2: addForm.value2, columnType: addFormColType }
          : f
      ));
      setEditingFilterId(null);
    } else {
      setFilters((prev) => [...prev, {
        id: Date.now().toString(),
        column: addForm.column,
        operator: addForm.operator,
        value: addForm.value,
        value2: addForm.value2,
        columnType: addFormColType,
      }]);
    }
    setPage(1);
    setAddForm(null);
  };

  const handleCancelForm = () => {
    setAddForm(null);
    setEditingFilterId(null);
  };

  const handleRemoveFilter = (id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const filteredEvents = applyFilters(events, filters);
  const visibleColumns = columns.filter((c) => c.visible);

  const getColWidth = (col: string) =>
    columnWidths[col] ?? DEFAULT_WIDTHS[col] ?? 120;

  const renderCell = (col: string, row: any): string => {
    if (col === "created_at") return formatDateTime(row.created_at);
    if (col === "time") return formatDateTime(row.time);
    if (col === "type") return row.type ?? "-";
    const val = row.data?.[col];
    if (val === undefined || val === null) return "-";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const ResizableHeader = ({ col }: { col: string }) => {
    const isSortable = SORTABLE.has(col);
    const isSorted = sortColumn === col;
    const width = getColWidth(col);

    return (
      <th
        onClick={() => isSortable && handleSort(col)}
        className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap select-none ${isSortable ? "hover:bg-gray-100 cursor-pointer" : ""}`}
        style={{ width, minWidth: width, maxWidth: width, overflow: "hidden" }}
      >
        <div className="flex items-center gap-1" style={{ overflow: "hidden" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
            {toLabel(col)}
          </span>
          {isSortable && isSorted && (
            <span className="text-blue-600 flex-shrink-0">{sortDirection === "ASC" ? "↑" : "↓"}</span>
          )}
        </div>
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
          onMouseDown={(e) => handleResizeStart(e, col)}
          onClick={(e) => e.stopPropagation()}
        />
      </th>
    );
  };

  const addFormColType = addForm ? detectColumnType(addForm.column, events) : "text";
  const addFormOps = addForm ? getOperators(addFormColType) : [];

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="bg-white rounded-xl flex flex-col"
          style={{ width: "92vw", maxWidth: 1300, height: "88vh", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 22 }}>📱</span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Mobile Tracking</h2>
                <p className="text-sm text-gray-500">
                  {patientName} &mdash;{" "}
                  {filters.length > 0
                    ? `${filteredEvents.length} of ${total} events (filtered)`
                    : `${total} events`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilterPanel((v) => !v)}
                className={`px-3 py-1.5 text-sm border rounded-md ${
                  showFilterPanel || filters.length > 0
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 hover:bg-gray-50 bg-white"
                }`}
              >
                Filter{filters.length > 0 ? ` (${filters.length})` : ""}
              </button>
              <button
                onClick={() => setShowColModal(true)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 bg-white"
              >
                Columns
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilterPanel && (
            <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 bg-gray-50 space-y-2">
              {/* Active filter chips */}
              {filters.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  {filters.map((f) => (
                    <span
                      key={f.id}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${editingFilterId === f.id ? "bg-blue-200 border-blue-400 text-blue-900" : "bg-blue-100 border-blue-200 text-blue-800"}`}
                    >
                      <button
                        onClick={() => openEditFilter(f)}
                        className="hover:underline text-left"
                        title="Edit filter"
                      >
                        {formatFilterLabel(f)}
                      </button>
                      <button
                        onClick={() => handleRemoveFilter(f.id)}
                        className="ml-1 text-blue-600 hover:text-red-600 font-bold leading-none"
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={handleClearAllFilters}
                    className="text-xs text-gray-500 hover:text-red-600 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Add filter form */}
              {addForm ? (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={addForm.column}
                    onChange={(e) => handleAddFormColumnChange(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    {columns.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <select
                    value={addForm.operator}
                    onChange={(e) =>
                      setAddForm((f) => f ? { ...f, operator: e.target.value, value: "", value2: "" } : null)
                    }
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    {addFormOps.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type={addFormColType === "date" ? "date" : addFormColType === "integer" ? "number" : "text"}
                    value={addForm.value}
                    onChange={(e) => setAddForm((f) => f ? { ...f, value: e.target.value } : null)}
                    placeholder="Value"
                    className="text-sm border border-gray-300 rounded px-2 py-1 w-36"
                  />
                  {addForm.operator === "between" && (
                    <>
                      <span className="text-sm text-gray-500">and</span>
                      <input
                        type={addFormColType === "date" ? "date" : "number"}
                        value={addForm.value2}
                        onChange={(e) => setAddForm((f) => f ? { ...f, value2: e.target.value } : null)}
                        placeholder="Value 2"
                        className="text-sm border border-gray-300 rounded px-2 py-1 w-36"
                      />
                    </>
                  )}
                  <button
                    onClick={handleConfirmAddFilter}
                    disabled={!addForm.value || (addForm.operator === "between" && !addForm.value2)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {editingFilterId ? "Save" : "Add"}
                  </button>
                  <button
                    onClick={handleCancelForm}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={openAddFilter}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Filter
                </button>
              )}
            </div>
          )}

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full text-red-500">{error}</div>
              ) : filteredEvents.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {events.length === 0 ? "No tracking events found" : "No events match the active filters"}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {visibleColumns.map((c) => <ResizableHeader key={c.id} col={c.id} />)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEvents.map((row, i) => (
                      <tr key={i} className="hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedRow(row)}>
                        {visibleColumns.map((c) => {
                          const w = getColWidth(c.id);
                          return (
                            <td
                              key={c.id}
                              className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                              style={{ width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" }}
                              title={renderCell(c.id, row)}
                            >
                              {renderCell(c.id, row)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-600">
              {total > 0
                ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`
                : "No results"}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1 || loading} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              <span className="px-3 py-1 text-sm text-gray-700">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages || loading} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        </div>
      </div>

      {/* Column manager modal — reuses the same component as PatientsScreen */}
      <ManageColumnsModal
        isOpen={showColModal}
        onClose={() => setShowColModal(false)}
        columns={columns}
        onSave={handleSaveColumns}
        onReset={handleResetColumns}
      />

      {/* Row detail popup */}
      {selectedRow && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedRow(null); }}
        >
          <div
            className="bg-white rounded-xl flex flex-col"
            style={{ width: 520, maxWidth: "90vw", maxHeight: "80vh", boxShadow: "0 8px 40px rgba(0,0,0,0.22)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Event Detail</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedRow.type ?? "—"} &middot; {formatDateTime(selectedRow.created_at)}</p>
              </div>
              <button
                onClick={() => setSelectedRow(null)}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {/* Fixed fields */}
              {[
                { label: "Created At", value: formatDateTime(selectedRow.created_at) },
                { label: "Device Time", value: formatDateTime(selectedRow.time) },
                { label: "Event Type", value: selectedRow.type ?? "—" },
              ].map(({ label, value }) => (
                <div key={label} className="flex gap-3 py-1.5 border-b border-gray-100 last:border-0">
                  <span className="w-36 flex-shrink-0 text-xs font-medium text-gray-500 uppercase tracking-wide pt-0.5">{label}</span>
                  <span className="text-sm text-gray-900 break-all">{value}</span>
                </div>
              ))}
              {/* Dynamic data fields */}
              {selectedRow.data && typeof selectedRow.data === "object" && Object.keys(selectedRow.data).length > 0 && (
                <>
                  <div className="pt-2 pb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Data Fields</span>
                  </div>
                  {Object.entries(selectedRow.data).map(([key, val]) => (
                    <div key={key} className="flex gap-3 py-1.5 border-b border-gray-100 last:border-0">
                      <span className="w-36 flex-shrink-0 text-xs font-medium text-gray-500 uppercase tracking-wide pt-0.5">{toLabel(key)}</span>
                      <span className="text-sm text-gray-900 break-all">
                        {val === null || val === undefined ? "—" : typeof val === "object" ? JSON.stringify(val) : String(val)}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
